/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT:
        1) Do not import from '/ezlibrary/exceptions/EzExceptions.js' to avoid circular dependencies.
        2) Do not import from '/ezlibrary/enums/EzEnumerations.js' to avoid circular dependencies.
        3) Do not import from '/ezlibrary/helpers/EzHelpers.j' to avoid circular dependencies.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import { EzBadParamException }  from '/ezlibrary/exceptions/EzBadParamException.js';

import { EzJSONSerializable } from '/ezlibrary/entities/EzJSONSerializable.js';

import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';
import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';

import { EzInstanceState } from '/ezlibrary/EzInstanceState.js';

/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES:
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Extending from EzClass Example/Template:
        1) Copy the template: /src/main/webapp/templates/EzSingletonClassTemplate.js
        2) Rename the file to your class name
        3) Search + Replace matching case 'EzMyClass' with your class name
        4) Search + release matching case 'ezMyClass' with your class's ezApi name.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

/**
 * @class
 * @description
 * Base class for ezClocker Website Javascript Classes
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * WARNING:
 *     1) Do not import /ezlibrary/exceptions/EzExceptions.js into this class
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzClass } from '/ezlibrary/EzClass.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with other ezClocker JS classes:
 *      import {
 *         EzClass,
 *         ... other supported classes ...
 *     } from ''/ezlibrary/ezclocker-classes/EzClasses.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzClass extends EzJSONSerializable {
    /**
     * @public @static @readonly @property
     * Gets the EzApi ready event name.
     *
     * @returns {string}
     */
    static get ezOnEzApiReadyEventName() {
        return 'onEzApiReady';
    }

    /**
     * @public @static @readonly @property
     * Gets the global reference of ezApi from globalThis.ezApi
     *
     * @returns {null|EzApi}
     */
    static get ezApiInstance() {
        return EzObject.assignOrNull(globalThis?.ezApi);
    }

    /**
     * @static
     * @public @method
     * Adds a document event listener for the provided readyEventName that calls the provided
     * ezRegistrator function.
     *
     * @param {string} readyEventName
     * @param {function} registratorCallback
     */
    static ezWaitReady(readyEventName, registratorCallback) {
        if (!EzString.hasText(readyEventName)) {
            throw new EzBadParamException(
                'readyEventName',
                EzClass,
                EzClass.ezWaitReady);
        }
        if (!EzFunction.isFunction(registratorCallback)) {
            throw new EzBadParamException(
                'registratorCallback',
                EzClass,
                EzClass.ezWaitReady);
        }

        globalThis.document.addEventListener(
            readyEventName,
            registratorCallback);
    }

    /**
     * @static
     * @public @method
     * Creates wait events for all the dependency classes in the provided dependencyClasses array
     * that have ezEventNames.onReady event name that call the provided ezApiRegistrationmethod.
     *
     * @param {array} ezApiDependencyClasses
     * Default: EzArray.EMPTY
     * @param {function} ezApiRegistrationMethod
     * Default: this.ezRegisterSingleton()
     */
    static ezWaitDependenciesReady(ezApiDependencyClazzes = EzArray.EMPTY, ezApiRegistrationMethod = null) {
        if (!EzFunction.isFunction(ezApiRegistrationMethod)) {
            throw new EzBadParamException(
                'ezApiRegistrationMethod',
                'EzClass',
                EzClass.ezWaitDependenciesReady);
        }

        EzClass.ezWaitReady(
            EzClass.ezOnEzApiReadyEventName,
            ezApiRegistrationMethod);

        if (EzArray.hasLength(ezApiDependencyClazzes)) {
            for (const ezApiDependencyClazz of ezApiDependencyClazzes) {
                if (EzString.hasLength(ezApiDependencyClazz?.ezEventNames?.onReady)) {
                    EzClass.ezWaitReady(
                        ezApiDependencyClazz.ezEventNames.onReady,
                        ezApiRegistrationMethod);
                }
            }
        }
    }

    /**
     * @static
     * @protected @readonly @property
     * Gets the name of this class
     *
     * @returns {string}
     */
    /* EXPERIMENTAL
    static get _ezClassName() {
        return 'function' === this.constructor.name
            ? this.name[0]
            : this.constructor.name;
    }
    */

    /**
     * @static
     * @protected @readonly @property
     * Gets the EzApi singleton instance name for this class.
     *
     * @returns {string}
     */
    /* EXPERIMENTAL
    static get _ezApiName() {
        return EzString.stringWithTextOrDefault(
            this?.ezApiName,
            `${className[0].toLowerCase()}${className.slice(1)}`);
    }
    */

    /**
     * @static
     * @protected @property @getter
     * Gets the EzApi singleton instance onReady event name for this class.
     *
     * @returns {string}
     */
    /* EXPERIMENTAL
    static get _onReadyEventName() {
        return EzString.stringWithTextOrDefault(
            this?.ezEventNames?.onReady,
            `ezOn_${this._ezClassName}_Ready`);
    }
    */

    /**
     * @static
     * @protected @readonly @property
     * Gets if the following is true or not:
     * 1) The global singleton instance for the EzApi class (globalThis?.ezApi or window?.ezApi) is a valid instance of EzApi and not undefined or null.
     * 2) The ready property (globalThis.ezApi?.ready) of the global singleton instance for the EzApi class (globalThis.ezApi or window.ezApi)
     *    is equal to true (globalThis.ezApi?.ready).
     * 3) The ezclocker property (globalThis.ezApi?.ezclocker) of the global singleton instance for the EzApi class (globalThis.ezApi or window.ezApi)
     *    is a valid object and not undefined or null.
     * 4) The following evaluation returns true for ALL of this classes defined dependencies that register singleton instances with EzApi:
     *    globalThis.ezApi.ezclocker?.[{DEFINED_EZAPI_DEPENDENCY_CLASS}.ezApiName]?.ready
     *
     * @param {undefined|null|array} ezApiDependencyClazzes
     *
     * @returns {boolean}
     */
    /* EXPERIMENTAL
    static _ezCanRegister(ezApiDependencyClazzes = EzArray.EMPTY) {
        if (!globalThis?.ezApi?.ready || !globalThis?.ezApi?.ezclocker) {
            return false;
        }

        if (EzArray.hasLength(ezApiDependencyClazzes)) {
            for (const ezApiDependencyClazz of ezApiDependencyClazzes) {
                if (!globalThis.ezApi.ezclocker?.[ezApiDependencyClazz?._ezApiName]?.ready) {
                    return false;
                }
            }
        }

        return true;
    }
    */

    /**
     * @static
     * @protected @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     *
     * @returns {boolean}
     */
    /* EXPERIMENTAL
    static get _ezIsRegistered() {
        return EzObject.isValid(this?.ezInstance) &&
            EzRegistrationState.REGISTERED === this?.ezApiRegistrationState;
    }
    */

    /**
     * @static
     * @protected @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     *
     * @param {undefined|null|array} ezApiDependencyClazzes
     *
     * @returns {boolean}
     */
    /* EXPERIMENTAL
    static _ezRegisterSingleton(ezApiDependencyClazzes = EzArray.EMPTY) {
        if (!EzBoolean.booleanOrFalse(this?._ezIsRegistered) && this?._ezCanRegister(ezApiDependencyClazzes)) {
            globalThis.ezApi.ezRegisterNewApi(this, this?._ezApiName);
        }

        return this._ezIsRegistered;
    }
    */

    /**
     * @static
     * @public @method
     * Creates wait events for all the dependency classes in the provided dependencyClasses array
     * that have ezEventNames.onReady event name that call the provided ezApiRegistrationmethod.
     *
     * @param {array} ezApiDependencyClazzes
     * Default: EzArray.EMPTY
     */
    /* EXPERIMENTAL
    static ezWaitEzApiDependenciesReady(ezApiDependencyClazzes = EzArray.EMPTY) {
        if (!this._ezIsRegistered) {
            this.ezApiRegistrationState = EzRegistrationState.PENDING;

            this.ezWaitReady(
                EzClass.ezOnEzApiReadyEventName,
                () => this._ezRegisterSingleton(ezApiDependencyClazzes));

            if (EzArray.hasLength(ezApiDependencyClazzes)) {
                for (const ezApiDependencyClazz of ezApiDependencyClazzes) {
                    if (EzString.hasLength(ezApiDependencyClazz?.ezEventNames?.onReady)) {
                        document.addEventListener(
                            readyEventName,
                            () => this._ezRegisterSingleton(ezApiDependencyClazzes));
                    }
                }
            }
        }
    }
    */

    /**
     * @public @constructor
     * Creates a new instance of this class.
     */
    constructor() {
        super();

        this.ezStates.push(EzInstanceState.CONSTRUCTED);
    }

    /**
     * @public @field
     * Stores the ready state of this instance.
     *
     * @type {boolean}
     */
    #ready = false;
    /**
     * @public @readonly @property
     * Gets if the EzApi singleton instance is registered with EzApi.
     *
     * @returns {boolean}
     */
    get ready() {
        /*return undefined !== globalThis?.ezApi?.ezclocker[this?.ezApiName] &&
            null != globalThis?.ezApi?.ezclocker[this?.ezApiName];*/
        return this.#ready;
    }
    /**
     * TODO: Remove after all attempts to set the ready property get removed.
     * @public @setter @property
     * Setter for the ready property is no longer useable. Ready property is now calculated.
     *
     * @param {boolean} _isReady
     *
     * @deprecated
     * Removing in a future release.
     */
    set ready(_isReady) {
        // Setter is now ignored
        this.#ready = EzBoolean.booleanOrFalse(_isReady);
    }

    /**
     * @public @field
     * Stores the EzInstanceState values for the instance.
     *
     * @type {array}
     */
    #ezStates = [];
    /**
     * @public @setter @property
     * Gets the EzInstanceState values for the instance.
     *
     * @returns {array}
     */
    get ezStates() {
        return this.#ezStates;
    }
    /**
     * @public @setter @property
     * Sets the EzInstanceState values for the instance.
     *
     * @param {array} states
     * Default: EzArray.EMPTY;
     */
    set ezStates(states = EzArray.EMPTY) {
        this.#ezStates = EzArray.arrayOrEmpty(states);

        globalThis?.console?.debug(
            `[Enumeration ${this.constructor.name}: ezStates Updated (${JSON.stringify(this.#ezStates)})]`);
    }

    /**
     * @override
     * @public @readonly @property
     * Gets this instance as a JSON Object (will no longer have the getter/setter methods).
     * Usefull for serailization to JSON.
     *
     * @returns {object}
     */
    get asJSONObject() {
        return {
            ready: this.ready,
            ezStates: this.ezStates
        };
    }

    /**
     * @override
     * @public @readonly @property
     * Gets this instance as JSON string (no formatting)
     *
     * @returns {String}
     */
    get asJSON() {
        return super.asJSON;
    }

    /**
     * @public @method
     * Initializes this instance.
     *
     * @returns {EzClass|object}
     */
    ezInit() {
        return this;
    }

    /**
     * @override
     * @public @method
     * Converts this instance into a JSON string with optional formatting.
     *
     * @param {undefined|null|Number} indentValue
     * @param {undefined|null|Boolean} htmlDisplay
     *
     * @returns {String}
     */
    ezToJSON(indentValue, htmlDisplay) {
        return super.ezToJSON(indentValue, htmlDisplay);
    }

    /**
     * @override
     * @public @method
     * Copies properties from the provided jsonObject to this instance (even if this instance does not define the property)
     *
     * @param {Object} jsonObject
     *
     * @returns {Object}
     * Returns this instance with the key & values from the provided jsonObject.
     */
    ezFromJSONObject(jsonObject) {
        return super.ezFromJSONObject(jsonObject);
    }

    /**
     * @override
     * @public @method
     * Converts the provided jsonString to a JSON object and then
     * passes that object to ezFromJSONObject() to copies properties to this instance
     * (even if this instance does not define the property)
     *
     * @param {String} jsonString
     *
     * @returns {Object}
     * Returns this instance with the key & values from the provided json string.
    */
    ezLoadFromJson(jsonString) {
        return super.ezLoadFromJson(jsonString);
    }
}
